import _ from "lodash";

import type {
	AttributeTableValue,
	StdFeatureProps,
	VectorDataLayerValuesMapping,
} from "../../../../apis/types/dataLayerTypes";
import { M2_TO_ACRES } from "../../../../utilities/constants";

/**
 * Reserved aerialFARMS ID for source attribute values the user has not assigned to a zone.
 */
export const UNASSIGNED_ID = 0;

/**
 * Reserved description for `UNASSIGNED_ID`. The API rejects any other description for ID 0.
 */
export const UNASSIGNED_DESCR = "unassigned";

/**
 * One row of the categorical assignment editor, holding only the user's mapping decisions for a
 * single source attribute value. These three fields are exactly the API's valuesMapping tuple.
 * Everything shown about a zone is derived from these rows, never stored on them.
 */
export interface AttributeTableRecord {
	/**
	 * Raw attribute table value.
	 */
	attributeTableValue: AttributeTableValue;
	/**
	 * aerialFARMS standard ID integer [0, numZones].
	 */
	stdId: number;
	/**
	 * Custom description of category (optional).
	 */
	catDesc?: string;
}

/**
 * A source (preprocessed GeoJSON) feature reduced to what zone aggregation reads from it.
 */
export interface ZoneSourceFeature {
	value: AttributeTableValue;
	areaM2: number;
}

/**
 * A zone as the API published it in the standardized GeoJSON, available when the configurator is
 * re-entered after a save.
 */
export interface PublishedZone {
	catDesc: string;
	/**
	 * Numeric value, or text value for a nominal column; null when the API published neither.
	 */
	catValue: AttributeTableValue | null;
	areaAcres: number;
}

/**
 * Per-zone aggregated display values for the Value and Acres columns, keyed by stdId.
 */
export interface ZoneDisplayValues {
	value: string;
	valueIsEstimate: boolean;
	areaAcres: number;
	areaIsEstimate: boolean;
	/**
	 * Per-attributeTableValue count of source GeoJSON features (stringified key).
	 */
	featureCountByAttrValue: Record<string, number>;
	/**
	 * Number of distinct attribute values mapped to this stdId (>1 means the same ID was selected
	 * for multiple rows).
	 */
	mappedAttrValueCount: number;
}

/**
 * Key joining an attribute table row to the source features carrying its value. Row values and
 * source feature values both originate from the same preprocessed GeoJSON, so stringifying lines
 * them up; a column mixing numbers with their own string spellings would collide, which no
 * supported source format produces.
 */
export function attrValueKey(value: AttributeTableValue): string {
	return String(value);
}

/**
 * True when the API will merge the features carrying one aerialFARMS ID into a single published
 * feature.
 *
 * This is the API's rule. `normalize_attribute_table` dissolves by aerialFARMS ID whenever two or
 * more features share an ID, so the per-ID source feature count is what decides it. The requested
 * zone count cannot: the API validates numZones against the request's own values mapping, never
 * against the uploaded file, and two distinct source values may legitimately map to one ID.
 */
function zoneWillDissolve(sourceFeatureCount: number): boolean {
	return sourceFeatureCount > 1;
}

/**
 * Default zone assignment for a fresh upload: the first `numZones` sorted unique attribute values
 * take IDs 1..numZones in order, and any remainder is unassigned.
 */
export function buildAttrTableRecords(
	sortedUniqueAttrVals: readonly AttributeTableValue[],
	numZones: number,
): AttributeTableRecord[] {
	return sortedUniqueAttrVals.map((attributeTableValue, i): AttributeTableRecord => {
		const stdId = i < numZones ? i + 1 : UNASSIGNED_ID;
		return {
			attributeTableValue,
			stdId,
			catDesc: stdId === UNASSIGNED_ID ? UNASSIGNED_DESCR : undefined,
		};
	});
}

/**
 * Index the API's published zones by aerialFARMS ID. Both API branches emit exactly one feature
 * per ID, so the first feature carrying an ID is the whole zone.
 */
export function indexPublishedZones(featureProps: readonly StdFeatureProps[]): Record<number, PublishedZone> {
	const result: Record<number, PublishedZone> = {};
	for (const props of featureProps) {
		if (props.aerialfarmsStd in result) continue;
		result[props.aerialfarmsStd] = {
			catDesc: props.catDesc,
			catValue: props.catValueNum ?? props.catValueText,
			areaAcres: props.areaM2 * M2_TO_ACRES,
		};
	}
	return result;
}

/**
 * Zone assignment for a round-trip edit, taken from the standardized GeoJSON the API published.
 * That mapping is authoritative, so it is adopted verbatim rather than recomputed.
 */
export function buildPublishedAttrTableRecords(
	rawValuesMapping: readonly VectorDataLayerValuesMapping[],
	publishedZonesByStdId: Record<number, PublishedZone>,
): AttributeTableRecord[] {
	return rawValuesMapping.map(([attributeTableValue, stdId]): AttributeTableRecord => {
		// Prefer the API's resolved description, which falls back to the zone's value when the user
		// entered none, over the null the request's mapping carries in that case.
		return {
			attributeTableValue,
			stdId,
			catDesc: publishedZonesByStdId[stdId]?.catDesc,
		};
	});
}

/**
 * Zone display values for a fresh upload, or for any state the user has edited since the last
 * save. Values and areas are local estimates of what the API will publish, flagged per zone so
 * the UI can mark them; a zone whose ID covers a single feature is not estimated, because the
 * API's area-weighted mean over one feature is that feature's own value.
 */
export function computeZoneDisplayValues(
	records: readonly AttributeTableRecord[],
	featuresByAttrValue: Record<string, ZoneSourceFeature[]>,
	options: { isQuantitative: boolean; colDecimals: number },
): Record<number, ZoneDisplayValues> {
	const { isQuantitative, colDecimals } = options;
	const result: Record<number, ZoneDisplayValues> = {};

	for (const [stdIdKey, recs] of Object.entries(_.groupBy(records, (rec) => rec.stdId))) {
		const featureCountByAttrValue: Record<string, number> = {};
		let sourceFeatureCount = 0;
		let totalAreaM2 = 0;
		let areaWeightedSum = 0;

		for (const rec of recs) {
			const key = attrValueKey(rec.attributeTableValue);
			const features = featuresByAttrValue[key] ?? [];
			featureCountByAttrValue[key] = features.length;
			sourceFeatureCount += features.length;
			for (const feature of features) {
				totalAreaM2 += feature.areaM2;
				areaWeightedSum += (typeof feature.value === "number" ? feature.value : 0) * feature.areaM2;
			}
		}

		const concatenated = recs.map((rec) => String(rec.attributeTableValue)).join("+");
		const isDissolving = zoneWillDissolve(sourceFeatureCount);
		const soleValue = recs.length === 1 ? recs[0].attributeTableValue : undefined;

		// Mirrors the API's area-weighted mean for a dissolved zone. It stays an estimate because
		// the API divides by the area of the *merged* geometry, which differs from the sum of the
		// source areas wherever the merge closes a gap or drops a sliver.
		let value = concatenated;
		let valueIsEstimate = false;
		if (isQuantitative && isDissolving) {
			value = totalAreaM2 > 0 ? (areaWeightedSum / totalAreaM2).toFixed(colDecimals) : concatenated;
			valueIsEstimate = true;
		} else if (isQuantitative && typeof soleValue === "number") {
			value = soleValue.toFixed(colDecimals);
		}

		result[Number(stdIdKey)] = {
			value,
			valueIsEstimate,
			areaAcres: totalAreaM2 * M2_TO_ACRES,
			areaIsEstimate: isDissolving,
			featureCountByAttrValue,
			mappedAttrValueCount: recs.length,
		};
	}

	return result;
}

/**
 * Zone display values for a round-trip edit the user has not touched yet. The API already ran the
 * dissolve, so these values are exact and nothing is estimated.
 */
export function computePublishedZoneDisplayValues(
	records: readonly AttributeTableRecord[],
	publishedZonesByStdId: Record<number, PublishedZone>,
	options: { colDecimals: number },
): Record<number, ZoneDisplayValues> {
	const result: Record<number, ZoneDisplayValues> = {};

	for (const [stdIdKey, recs] of Object.entries(_.groupBy(records, (rec) => rec.stdId))) {
		const stdId = Number(stdIdKey);
		const published = publishedZonesByStdId[stdId] as PublishedZone | undefined;
		const catValue = published?.catValue ?? recs[0].attributeTableValue;
		result[stdId] = {
			value: typeof catValue === "number" ? catValue.toFixed(options.colDecimals) : String(catValue),
			valueIsEstimate: false,
			areaAcres: published?.areaAcres ?? 0,
			areaIsEstimate: false,
			featureCountByAttrValue: Object.fromEntries(recs.map((rec) => [attrValueKey(rec.attributeTableValue), 1])),
			mappedAttrValueCount: recs.length,
		};
	}

	return result;
}
